import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import type {
    RevertBlocker,
    RevertChildManualEvent,
    RevertGroupEffect,
    RevertPreview,
} from '@/domain/fishGroups';
import { formatDateTimeWorded } from '@/utils/date';

import styles from './RevertOperationModal.module.scss';

interface Props {
    preview: RevertPreview;
}

/** What the revert will do and what blocks it. */
export default function RevertPreviewSections(props: Props) {
    const { preview } = props;
    const { t } = useTranslation('fishGroups');
    const groupName = groupNames(preview);

    return (
        <>
            {preview.blockers.length > 0 && <BlockersSection blockers={preview.blockers} />}
            <div className={styles.effects}>
                <EffectsSection
                    heading={t('actions.revert.restoredHeading')}
                    groups={preview.restoredFishGroups}
                />
                <EffectsSection
                    heading={t('actions.revert.discardedHeading')}
                    groups={preview.discardedFishGroups}
                />
            </div>
            {preview.childManualEvents.length > 0 && (
                <ChildEventsSection events={preview.childManualEvents} groupName={groupName} />
            )}
        </>
    );
}

function groupNames(preview: RevertPreview): Map<number, string> {
    return new Map(
        [...preview.restoredFishGroups, ...preview.discardedFishGroups].map((group) => [
            group.fishGroupId,
            group.fishGroupName,
        ])
    );
}

function BlockersSection({ blockers }: { blockers: RevertBlocker[] }) {
    const { t } = useTranslation('fishGroups');
    return (
        <Alert tone="danger" size="small">
            <div className={styles.alertBody}>
                <h5 className={styles.sectionHeading}>{t('actions.revert.blockers.heading')}</h5>
                <ul className={styles.list}>
                    {blockers.map((blocker) => (
                        <li key={blocker.code}>
                            {t(`actions.revert.blockers.${blockerKey(blocker.code)}`, {
                                operations: blocker.operationIds.join(', '),
                                cages: blocker.cageIds.join(', '),
                            })}
                        </li>
                    ))}
                </ul>
            </div>
        </Alert>
    );
}

// Backend codes are SCREAMING_SNAKE; translation keys are camelCase.
function blockerKey(code: RevertBlocker['code']): string {
    return code.toLowerCase().replace(/_([a-z])/g, (_, ch: string) => ch.toUpperCase());
}

function EffectsSection({ heading, groups }: { heading: string; groups: RevertGroupEffect[] }) {
    const { t } = useTranslation('fishGroups');
    return (
        <section className={styles.section}>
            <h5 className={styles.sectionHeading}>{heading}</h5>
            {groups.length === 0 ? (
                <p className={styles.sectionDescription}>{t('actions.revert.none')}</p>
            ) : (
                <ul className={styles.list}>
                    {groups.map((group) => (
                        <li key={group.fishGroupId}>
                            {t('actions.revert.groupInCage', {
                                name: group.fishGroupName,
                                cage: group.cageName,
                            })}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

interface ChildEventsSectionProps {
    events: RevertChildManualEvent[];
    groupName: Map<number, string>;
}

function ChildEventsSection({ events, groupName }: ChildEventsSectionProps) {
    const { t } = useTranslation('fishGroups');
    return (
        // A note on what the revert leaves behind, read with the rest of the preview, so it is
        // not announced on its own.
        <Alert tone="warning" size="small" live="off">
            <div className={styles.alertBody}>
                <h5 className={styles.sectionHeading}>{t('actions.revert.childEvents.heading')}</h5>
                <p className={styles.sectionDescription}>
                    {t('actions.revert.childEvents.description')}
                </p>
                <ul className={styles.list}>
                    {events.map((event) => (
                        <li key={event.id}>
                            {t('actions.revert.childEvents.item', {
                                type: t(`eventTypes.${event.eventType}`),
                                date: formatDateTimeWorded(event.eventDate, 'minute'),
                                group: groupName.get(event.fishGroupId) ?? event.fishGroupId,
                            })}
                        </li>
                    ))}
                </ul>
            </div>
        </Alert>
    );
}
